import {
  deleteField,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  query,
  where,
  runTransaction,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  ffeSchedulesDB,
  productsDB,
  uploadAndSave,
  userProductsDB,
} from "../../libs/firebase";
import { deepClone } from "../../libs/util";
import { getProjectSchemeById } from "./projectsSchemeApi";
import {
  canEditUserProductIdentity,
  updateUserProductIdentity,
  type ProductIdentityPatch,
} from "./userProductsApi";
import { getSearchApiBaseUrl } from "../../design_stream/components/apiConfig";
import { isRealPrice } from "../../Home/schedules/utils/pricing";
import {
  declareSection,
  isNoCategoryName,
} from "../../Home/schedules/utils/grouping";
import { isDecorativeAccessory } from "../../Home/schedules/utils/scheduleEligibility";
import { scheduleItemThumbnail } from "../../Home/schedules/utils/scheduleThumbnail";
import { generateID, getActiveDesignFirmId, getPreset, firebaseHelper } from "ui";
import { hasAccessToResource } from "ui/utils/usePermissions";
import { ensureSharingV2Flag } from "ui/utils/useDesignFirm";
import { sharingV2Doc, sharingV2Enabled } from "ui/utils/workspace/sharingV2";
import { setItemProject } from "ui/utils/workspace/accessApi";
import { teamScopeConstraints, type TeamScope } from "./accessQueries";
import { carryAccessFields } from "./accessFields";
import { flaggedProjectFirm } from "./createFirm";
import { teamAnalytics, teamItem, teammateItem } from "../../team/teamAnalytics";
import {
  FFEScheduleDimensions,
  FFEScheduleInterface,
  FFEScheduleItem,
  FFEScheduleItemSource,
  FFEScheduleLeadTime,
  NewProductMetadata,
  ObjectStatusType,
  Permissions,
  ProductInterface,
  ProductType,
  ProjectSchemeInterface,
  ResourceAccessFields,
  canonicalizeMaterialCategory,
  validateProduct,
} from "ts-interfaces";

const FFE_SCHEDULES_COLLECTION = "ffeSchedules";

// SECURITY: the ffeSchedules rules block lives in the centralized rules
// (firebase/rules/common.rules) — owner / team via design_firm_id / is_public
// reads, owner-only writes, with emulator coverage in
// firebase/tests/rules_equivalence.test.ts. It gates reads (schedules carry
// trade pricing / margins), not just writes.
// KNOWN GAP: per-user sharing via permissions.ffeScheduleRead/ffeScheduleWrite
// is NOT honored by those rules — the permissions ACL arrays are self-writable,
// so trusting them server-side would let anyone self-grant access. The "shared"
// branch of getFFESchedulesByProject therefore returns nothing until the ACL
// model is hardened (firebase/RULES-HARDENING-FINDINGS.md P2-7). Owner, team,
// and public paths work today.

/** Thrown by saveFFESchedule when another tab or co-editor wrote in between. */
export class FFEScheduleConflictError extends Error {
  constructor() {
    super("This schedule was changed elsewhere");
    this.name = "FFEScheduleConflictError";
  }
}

// deepClone (JSON round-trip) strips undefined-valued keys Firestore rejects;
// optional fields are cleared by setting undefined — this holds because
// saveFFESchedule writes with tx.set (whole-doc replacement), so a stripped
// key genuinely disappears. Also drops the hydrated `products` view field
// (HydratedFFESchedule) so a populated schedule passed back to save can never
// write product docs into the schedule document.
const payload = (value: unknown): any => {
  const data = deepClone(value);
  delete data.products;
  return data;
};

/** Initial doc for a new, empty schedule — uid + projectId are required. */
export function createNewFFESchedule(
  uid: string,
  projectId: string,
): FFEScheduleInterface {
  if (!uid) throw new Error("uid is required to create an FF&E schedule");
  if (!projectId)
    throw new Error("projectId is required to create an FF&E schedule");
  const now = Date.now();
  return {
    id: generateID(),
    name: "",
    description: "",
    uid,
    design_firm_id: getActiveDesignFirmId(uid),
    projectId,
    is_public: false,
    writeAccess: [],
    // Teams sharing: a create carries its creator alone; the project decides
    // the rest once it is filed (schedules have no list of their own).
    created_by: uid,
    readers: [uid],
    editors: [uid],
    coverImage: "",
    items: [],
    currency: "USD",
    presentation: {
      units: "mm",
      viewMode: "summary",
      columnVisibility: {},
    },
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Upsert a whole schedule (first write and debounced editor commits alike),
 * with an optimistic-concurrency check on `updatedAt` — throws
 * FFEScheduleConflictError instead of clobbering another writer.
 * `baseUpdatedAt` may only be omitted when CREATING the doc: once it exists,
 * omitting the base would silently downgrade the whole-doc tx.set below to
 * last-write-wins, so it throws instead.
 *
 * `updatedAt` is this collection's VERSION TOKEN: it is what the check above
 * compares, and every writer must bump it. A writer that edits `items` without
 * bumping updatedAt (see packages/schemas/backfill_schedule_thumbs.ts) is
 * invisible to this check in both directions — an editor open at the time keeps
 * a base that still matches, so its next whole-doc set silently reverts that
 * writer's change.
 *
 * No client-side write-ACL check here (same convention as saveMaterialSheet):
 * firestore.rules is the write boundary — callers reach a schedule through the
 * read-gated load paths, and archiveFFESchedule's client-side check is UX only.
 */
export async function saveFFESchedule(
  schedule: FFEScheduleInterface,
  baseUpdatedAt?: number,
): Promise<FFEScheduleInterface> {
  const now = Date.now();
  // createdAt/updatedAt are analytics-load-bearing (retention = users coming
  // back to edit): updatedAt bumps on EVERY write, createdAt is immutable.
  const next = {
    ...schedule,
    createdAt: schedule.createdAt || now,
    updatedAt: now,
  };
  // Only a create skips the base. Teams sharing: one in a flagged project
  // takes its team, even from another workspace.
  if (baseUpdatedAt == null) {
    const firm = await flaggedProjectFirm(next.projectId, next.uid);
    if (firm) next.design_firm_id = firm;
  }
  const data = payload(next);
  const ref = doc(firebaseHelper.db, FFE_SCHEDULES_COLLECTION, next.id);
  let createdAt = next.createdAt;
  let created = false;
  await runTransaction(firebaseHelper.db, async (tx) => {
    const snap = await tx.get(ref);
    created = !snap.exists();
    if (snap.exists()) {
      const stored = snap.data() as FFEScheduleInterface;
      // Only creation may skip the base. A deferred save that lost track of its
      // base would otherwise blind-overwrite the whole document. This is an
      // invariant guard, not an error path — every editor path seeds a base
      // from load() before it can commit. It stays phrased as user copy because
      // commitAddedProduct's catch renders `e.message` straight into a
      // snackbar; commit()'s catch, by contrast, can't tell this from a network
      // failure and would re-queue the same doomed payload, which is the reason
      // this must remain unreachable rather than merely well-worded.
      if (baseUpdatedAt == null)
        throw new Error(
          "Couldn't save the schedule — please reload the page and try again.",
        );
      if (stored.updatedAt !== baseUpdatedAt)
        throw new FFEScheduleConflictError();
      // The stored createdAt wins over whatever the caller's in-memory copy
      // carries — the whole-doc set below must never rewrite or drop it.
      if (stored.createdAt) createdAt = stored.createdAt;
      // Same for the backend-owned access fields: a stale copy must not
      // revert a grant through this whole-doc replacement.
      carryAccessFields(data, stored as unknown as Record<string, unknown>);
    }
    data.createdAt = createdAt;
    // Whole-doc replacement, never tx.update: update() leaves keys absent
    // from the payload untouched, which would resurrect fields the caller
    // cleared to undefined (payload() strips them).
    tx.set(ref, data);
  });
  if (!created) teamAnalytics.itemEdited(teammateItem("schedule", next.id, next, "editor"));
  if (created && sharingV2Doc(next)) return refileCreated({ ...next, createdAt });
  if (created) trackScheduleFiled(next);
  return { ...next, createdAt };
}

const trackScheduleFiled = (schedule: FFEScheduleInterface) =>
  teamAnalytics.itemFiled(teamItem("schedule", schedule.id, schedule, "editor"), {
    action: "filed",
    projectId: schedule.projectId ?? null,
  });

// Teams sharing: a create carries its creator's arrays alone; the backend
// derives the project's audience. Its write bumps updatedAt, the editor's
// version token, so the caller gets the stored value back. On failure the
// schedule stays private to its creator.
async function refileCreated(
  schedule: FFEScheduleInterface,
): Promise<FFEScheduleInterface> {
  try {
    const { readers, editors } = await setItemProject(
      "schedule",
      schedule.id,
      schedule.projectId,
      schedule.design_firm_id,
    );
    const snap = await getDoc(
      doc(firebaseHelper.db, FFE_SCHEDULES_COLLECTION, schedule.id),
    );
    const stored = snap.data() as FFEScheduleInterface | undefined;
    trackScheduleFiled(schedule);
    return {
      ...schedule,
      readers,
      editors,
      updatedAt: stored?.updatedAt ?? schedule.updatedAt,
    };
  } catch (e) {
    console.warn("[saveFFESchedule] filing the new schedule failed:", e);
    return schedule;
  }
}

/**
 * In-place single/few-field update via updateDoc (repo convention). A key
 * explicitly set to `undefined` DELETES that field from the doc — updateDoc
 * merges, so simply omitting the key would leave the old value in place.
 */
export async function updateFFESchedule(
  scheduleId: string,
  partial: Partial<FFEScheduleInterface>,
): Promise<void> {
  if (!scheduleId) throw new Error("Invalid schedule id");
  const update: Record<string, any> = { updatedAt: Date.now() };
  for (const [key, value] of Object.entries(partial)) {
    // updatedAt always bumps; createdAt is immutable (retention analytics
    // reads both); id must always match the document key (list dedup and every
    // doc() lookup key off it) — a partial can neither rewrite nor delete them.
    if (
      key === "products" ||
      key === "updatedAt" ||
      key === "createdAt" ||
      key === "id"
    )
      continue;
    update[key] = value === undefined ? deleteField() : deepClone(value);
  }
  await updateDoc(
    doc(firebaseHelper.db, FFE_SCHEDULES_COLLECTION, scheduleId),
    update,
  );
}

// Schedule + live product docs keyed by refId (populateProducts option).
// View-only: never persisted — payload() strips `products` on save. A missing
// key = the ref no longer resolves (UI falls back to item.cachedThumbnail).
export type HydratedFFESchedule = FFEScheduleInterface & {
  products?: Record<string, ProductInterface>;
};

// Product source → backing collection: the ONE dispatch table every
// source-qualified read (hydration, bulk adds) goes through. A new
// FFEScheduleItemSource means adding exactly one entry here.
// The values are THUNKS, not refs: the refs are `export let` bindings, null
// until setupHelpers() runs, so a module-scope snapshot freezes null (#3757).
const SOURCE_DB: Record<FFEScheduleItemSource, () => any> = {
  catalog: () => productsDB,
  userProduct: () => userProductsDB,
};
const SOURCES = Object.keys(SOURCE_DB) as FFEScheduleItemSource[];

/**
 * Every schedule surface reads brand from `product.supplier` and the link
 * from `product.productLink`, but the material/paint creators (My Library
 * uploads, the sidebar) write them to `metadata.brand` / `metadata.url`
 * ONLY — the identity editor keeps both in sync, the creators never set the
 * top-level fields. Fill the gap on read so a My Library material lands on
 * the schedule with its brand and link, whichever path added it; an edit
 * then writes both fields through the identity patch.
 */
const mirrored = (top: unknown, mirror: unknown): string | undefined => {
  if (typeof top === "string" && top.trim()) return undefined;
  return typeof mirror === "string" && mirror.trim()
    ? mirror.trim()
    : undefined;
};
const withIdentityMirrors = (product: ProductInterface): ProductInterface => {
  const md = (product as any)?.metadata;
  const supplier = mirrored(product.supplier, md?.brand);
  const productLink = mirrored(product.productLink, md?.url);
  if (supplier === undefined && productLink === undefined) return product;
  return {
    ...product,
    ...(supplier !== undefined ? { supplier } : {}),
    ...(productLink !== undefined ? { productLink } : {}),
  };
};

/** Batch-fetch product docs by `id` field, 30 per `in` query (Firestore limit). */
export async function fetchByIds(
  db: any,
  ids: string[],
): Promise<ProductInterface[]> {
  if (ids.length === 0) return [];
  // A missing ref is a bug, not an empty result — [] reads downstream as
  // "these products are gone" and silently drops the add.
  if (!db) throw new Error("Product catalog unavailable — please reload.");
  const results: ProductInterface[] = [];
  for (let i = 0; i < ids.length; i += 30) {
    const snap = await getDocs(
      query(db, where("id", "in", ids.slice(i, i + 30))),
    );
    snap.forEach((d: any) =>
      results.push(withIdentityMirrors(d.data() as ProductInterface)),
    );
  }
  return results;
}

/**
 * Source-qualified batch read for the editor store's bulk adds: the same
 * `${source}:${id}` map shape getFFEScheduleById hydrates with, 30 ids per
 * query, missing refs simply absent. Prefer this over per-id getDocByID for
 * anything more than one product — it also resolves pipeline-written docs
 * whose doc ID differs from their `id` field.
 */
export async function getScheduleProducts(
  refs: ScheduleProductRef[],
): Promise<Record<string, ProductInterface>> {
  const products: Record<string, ProductInterface> = {};
  await Promise.all(
    SOURCES.map(async (source) => {
      const ids = [
        ...new Set(
          refs.filter((r) => r.source === source && r.id).map((r) => r.id),
        ),
      ];
      const docs = await fetchByIds(SOURCE_DB[source](), ids);
      for (const p of docs) products[`${source}:${p.id}`] = p;
    }),
  );
  return products;
}

/**
 * Load one schedule; null when missing, throws when read access is denied.
 * `populateProducts` attaches the live product docs for every item ref.
 */
export async function getFFEScheduleById(
  scheduleId: string,
  userId: string,
  permissions: Permissions | null,
  opts?: { populateProducts?: boolean },
): Promise<HydratedFFESchedule | null> {
  if (!scheduleId) throw new Error("Invalid schedule id");
  const docSnap = await getDoc(
    doc(firebaseHelper.db, FFE_SCHEDULES_COLLECTION, scheduleId),
  );
  if (!docSnap.exists()) return null;
  const data = docSnap.data() as FFEScheduleInterface;
  // Teams sharing: the schedule's team flag decides which check applies,
  // here and in the editor's write check that follows.
  await ensureSharingV2Flag(firebaseHelper.db, data.design_firm_id);
  // Public schedules are readable by anyone — including signed-out share
  // views with no userId/permissions — mirroring the planned rules read
  // clause. The ACL check only gates non-public docs.
  const hasReadAccess =
    data.is_public === true ||
    hasAccessToResource(
      permissions,
      scheduleId,
      "read",
      "ffeSchedule",
      data.uid || "",
      userId,
      data.design_firm_id,
      data,
    );
  if (!hasReadAccess)
    throw new Error("You don't have permission to access this schedule");
  if (!opts?.populateProducts) return data;

  const idsFor = (source: FFEScheduleItemSource) => [
    ...new Set(
      (data.items || [])
        .filter((i) => i.source === source && i.refId)
        .map((i) => i.refId),
    ),
  ];
  const fetched = await Promise.all(
    SOURCES.map((source) => fetchByIds(SOURCE_DB[source](), idsFor(source))),
  );
  // Source-qualified keys — the same refId may exist in BOTH collections
  // (addProductToSchedule permits it), so bare-id keys would collide.
  const products: Record<string, ProductInterface> = {};
  SOURCES.forEach((source, i) => {
    for (const p of fetched[i]) products[`${source}:${p.id}`] = p;
  });
  return { ...data, products };
}

/**
 * All schedules attached to a project that the caller can read, newest
 * first, archived excluded: the project's team's readers-scoped query, or the
 * caller's own for a Personal project (sorted client-side; lists are small).
 */
export async function getFFESchedulesByProject(
  projectId: string,
  userId: string,
  _permissions: Permissions | null,
  _activeFirmId?: string | null,
  teamScope: TeamScope | null = null,
): Promise<FFEScheduleInterface[]> {
  if (!projectId || !userId || !ffeSchedulesDB) return [];
  // A team project's schedules come from its team's readers-scoped query (the
  // active team, or the project's own when it belongs to another team the user
  // is in); a failure (a missing index, say) propagates instead of painting an
  // empty list. A Personal project's are the user's own: Personal is owner-only.
  const project = await getProjectSchemeById(projectId);
  const firmId = project ? project.design_firm_id || null : teamScope?.firmId ?? null;
  const constraints = firmId
    ? teamScopeConstraints({ uid: userId, firmId })
    : [where("uid", "==", userId)];
  const snapshot = await getDocs(
    query(ffeSchedulesDB, where("projectId", "==", projectId), ...constraints),
  );
  return snapshot.docs
    .map((d) => d.data() as FFEScheduleInterface)
    .filter((s) => !s.archived)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * All non-archived schedules in the workspace the caller can read (the team
 * scope's, or the caller's own in Personal), newest first. Powers
 * cross-project surfaces — the dashboard's Recent Schedule card and the
 * getting-started checklist. Client-side sort; per-user counts are small.
 */
export async function getFFESchedulesForUser(
  userId: string,
  _activeFirmId?: string | null,
  teamScope: TeamScope | null = null,
): Promise<FFEScheduleInterface[]> {
  if (!userId || !ffeSchedulesDB) return [];
  if (teamScope) {
    const snapshot = await getDocs(
      query(ffeSchedulesDB, ...teamScopeConstraints(teamScope)),
    );
    return snapshot.docs
      .map((d) => d.data() as FFEScheduleInterface)
      .filter((s) => !s.archived)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }
  // Personal: the user's own schedules (Personal is owner-only).
  try {
    const snapshot = await getDocs(query(ffeSchedulesDB, where("uid", "==", userId)));
    return snapshot.docs
      .map((d) => d.data() as FFEScheduleInterface)
      .filter((s) => !s.archived)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (error) {
    console.error("Error fetching user FF&E schedules:", error);
    return [];
  }
}

/** Schedules are never hard-deleted — archiving flips the flag (reversible). */
export async function archiveFFESchedule(
  schedule: FFEScheduleInterface,
  permissions: Permissions | null,
  archived: boolean = true,
): Promise<void> {
  if (!schedule?.id) throw new Error("Schedule id is required");
  const user = getAuth().currentUser;
  const hasWrite = hasAccessToResource(
    permissions,
    schedule.id,
    "write",
    "ffeSchedule",
    schedule.uid || "",
    user?.uid || "",
    schedule.design_firm_id,
    schedule,
  );
  if (!hasWrite)
    throw new Error("You do not have permission to archive this schedule");
  await updateFFESchedule(schedule.id, { archived });
}

/**
 * Can `uid` file a schedule (create or copy one) into this project? Owner and
 * projectWrite-ACL grants qualify via hasAccessToResource; members of the
 * project's team qualify because a schedule filed there is stamped with the
 * project's design_firm_id and is readable/listable by the whole team. A
 * projectRead-ONLY share does NOT qualify — the write would create a schedule
 * the project's owner can neither see nor manage (the per-user ffeSchedule
 * ACL is not honored by the rules; see the header of this file).
 * UX-only, like archiveFFESchedule's check — firestore rules stay the boundary.
 */
export function canFileScheduleInProject(
  project: Pick<ProjectSchemeInterface, "id" | "owner" | "design_firm_id"> &
    ResourceAccessFields,
  uid: string,
  permissions: Permissions | null,
): boolean {
  if (!uid) return false;
  // Teams sharing: edit on the project, from its editors array.
  if (project.design_firm_id && sharingV2Enabled(project.design_firm_id)) {
    return hasAccessToResource(
      permissions,
      project.id,
      "write",
      "project",
      project.owner || "",
      uid,
      project.design_firm_id,
      project,
    );
  }
  // A Personal project: only its owner.
  return project.owner === uid;
}

/**
 * Copy a schedule into another (or the same) project as a NEW doc owned by
 * the copier — the duplicateCollectionScheme convention. Items copy verbatim:
 * they reference productsV2/userProducts by id, nothing project-scoped.
 * Sharing state (is_public, writeAccess) is deliberately reset so a copy can
 * never leak a live share link or ACL.
 *
 * The source is RE-FETCHED by id — the caller's card snapshot can be stale
 * (another tab or a co-editor may have edited since the list loaded), and a
 * copy silently missing those rows would look complete. The copy's
 * design_firm_id comes from the TARGET PROJECT, not the copier's active
 * workspace: a copy filed in a team project must be visible to that team,
 * and a copy filed in a personal project must not leak to whatever team the
 * copier happens to have active.
 */
export async function copyFFEScheduleToProject(
  schedule: FFEScheduleInterface,
  targetProjectId: string,
  uid: string,
  permissions: Permissions | null,
): Promise<FFEScheduleInterface> {
  if (!schedule?.id) throw new Error("Schedule id is required");
  if (!targetProjectId) throw new Error("Target project id is required");
  if (!uid) throw new Error("uid is required");
  const [fresh, project] = await Promise.all([
    getFFEScheduleById(schedule.id, uid, permissions),
    getProjectSchemeById(targetProjectId),
  ]);
  if (!fresh) throw new Error("Schedule not found");
  if (!project) throw new Error("Project not found");
  if (!canFileScheduleInProject(project, uid, permissions))
    throw new Error(
      "You don't have permission to add schedules to this project",
    );
  const now = Date.now();
  const sameProject = targetProjectId === fresh.projectId;
  return saveFFESchedule({
    ...payload(fresh),
    id: generateID(),
    // A same-project copy needs a distinguishing name; a cross-project copy
    // is still "the" schedule over there.
    name: sameProject
      ? `Copy of ${fresh.name || "Untitled Schedule"}`
      : fresh.name,
    uid,
    design_firm_id: project.design_firm_id ?? null,
    projectId: targetProjectId,
    is_public: false,
    writeAccess: [],
    // The copy starts private to the copier; no own list on a schedule.
    created_by: uid,
    access: undefined,
    readers: [uid],
    editors: [uid],
    archived: false,
    createdAt: now,
    updatedAt: now,
  });
}

// ---------------- product → item mapping ----------------

// Trim + sentinel guard for a caller-supplied Section: "Uncategorized" (any
// case) means "no Section", never a literal override — it would merge with
// the derived bucket while persisting a divergent state.
const normalizeCategoryOverride = (
  v: string | undefined,
): string | undefined => {
  const trimmed = v?.trim() || undefined;
  return isNoCategoryName(trimmed) ? undefined : trimmed;
};

const LEAD_TIME_OPTIONS = [
  "In Stock",
  "1-2 weeks",
  "2-4 weeks",
  "4-6 weeks",
  "6-8 weeks",
  "8-10 weeks",
  "10-12 weeks",
  "12-14 weeks",
  "14-16 weeks",
  "16-18 weeks",
] as const satisfies readonly FFEScheduleLeadTime[];

// Freeform product lead time → canonical option (en-dash normalized), else skip.
const normalizeLeadTime = (v: unknown): FFEScheduleLeadTime | undefined => {
  if (typeof v !== "string") return undefined;
  const n = v.replace(/–/g, "-").trim();
  return (LEAD_TIME_OPTIONS as readonly string[]).includes(n)
    ? (n as FFEScheduleLeadTime)
    : undefined;
};

// Documented unit-of-measure set (docs/ffe-schedule-ui-migration.md §3);
// freeform metadata.priceUnit ("per box", "sqm", …) outside it seeds 'each'.
const UNIT_OPTIONS = [
  "each",
  "sqft",
  "linft",
  "yard",
  "roll",
  "gallon",
  "set",
] as const;

const normalizeUnit = (v: unknown): string => {
  const n = typeof v === "string" ? v.trim().toLowerCase() : "";
  return (UNIT_OPTIONS as readonly string[]).includes(n) ? n : "each";
};

// Best-effort text seed: strings only, trimmed; anything else stays undefined
// so the key is stripped before the Firestore write.
const seedText = (v: unknown): string | undefined => {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t || undefined;
};

// The web clipper stores price as `unitPrice: { amount, currency, unit }`
// with a free-text priced-per unit ("each", "per sq ft", "square metre"…).
// Only units that map onto the row's own vocabulary seed — a per-m² price
// filed as "each" would misstate the quote, so those stay blank. The LLM's
// spelling varies between runs of the same page ("sq ft", "sq.ft", "sq. ft.",
// "per sq. ft."), so lookup is on a punctuation-free key: `per ` and a
// plural s stripped, periods and slashes dropped, spaces collapsed.
const CLIP_UNIT_ALIASES: Record<string, (typeof UNIT_OPTIONS)[number]> = {
  each: "each",
  ea: "each",
  piece: "each",
  pc: "each",
  item: "each",
  unit: "each",
  sqft: "sqft",
  "sq ft": "sqft",
  "square foot": "sqft",
  "square feet": "sqft",
  sf: "sqft",
  ft2: "sqft",
  "ft²": "sqft",
  linft: "linft",
  "lin ft": "linft",
  "linear foot": "linft",
  "linear feet": "linft",
  lf: "linft",
  yard: "yard",
  yd: "yard",
  "linear yard": "yard",
  roll: "roll",
  gallon: "gallon",
  gal: "gallon",
  set: "set",
};
const mapClipUnit = (v: unknown): string | undefined => {
  if (typeof v !== "string") return undefined;
  let key = v
    .toLowerCase()
    .replace(/[./]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^per /, "");
  if (!key) return undefined;
  if (CLIP_UNIT_ALIASES[key]) return CLIP_UNIT_ALIASES[key];
  key = key.replace(/s$/, "");
  return CLIP_UNIT_ALIASES[key];
};

// Same currency rule as metadata.listPrice: seed when the clip's currency is
// unknown or matches the schedule's. A symbol ("€") is neither, so it never
// seeds — the amount can't be trusted to be in the schedule's currency.
const seedClipPrice = (
  product: ProductInterface,
  scheduleCurrency: string | undefined,
): { listPrice: number; unit: string } | undefined => {
  const up = (product as any)?.unitPrice;
  if (!up || typeof up !== "object" || !isRealPrice(up.amount))
    return undefined;
  const currency =
    typeof up.currency === "string" ? up.currency.trim().toUpperCase() : "";
  if (currency && currency !== (scheduleCurrency || "USD").toUpperCase())
    return undefined;
  const unit =
    up.unit == null || up.unit === "" ? "each" : mapClipUnit(up.unit);
  if (!unit) return undefined;
  return { listPrice: up.amount, unit };
};

// Material: the taxonomy sub-category first (catalog), then the clipper's
// composition text ("Oak, Brass"), then a My Library upload's own category —
// canonicalized, which returns nothing for fixture object types ("Taps"), the
// reason a raw product.category fallback was never safe. Paints skip the
// category fallback: "Paint" as a paint's Material says nothing.
const seedMaterial = (
  product: ProductInterface,
  md: Partial<NewProductMetadata>,
): string | undefined => {
  const fromTaxonomy = seedText(md.subCategory);
  if (fromTaxonomy) return fromTaxonomy;
  const contents = seedText((md as any).materialContents);
  if (contents) return contents;
  if (product?.productType === ProductType.PAINT) return undefined;
  const raw = Array.isArray(product?.category)
    ? product.category[0]
    : product?.category;
  return canonicalizeMaterialCategory(seedText(raw));
};

// Paints made in the paint creator store their sheen as a render preset key
// (materialData.preset). The preset's display name is the finish a schedule
// wants ("Satin"); an unknown key seeds nothing rather than a slug.
const seedPaintFinish = (product: ProductInterface): string | undefined => {
  if (product?.productType !== ProductType.PAINT) return undefined;
  const key = product?.materialData?.preset;
  if (typeof key !== "string" || !key) return undefined;
  return seedText(getPreset(key)?.name);
};

// product.color holds lowercase search tags ('gray'); Title-case them for the
// user-facing Color field. Capped at 3 so a tag-heavy product stays readable.
const seedColor = (colors: unknown): string | undefined => {
  if (!Array.isArray(colors)) return undefined;
  const names = [
    ...new Set(
      colors
        .map(seedText)
        .filter((c): c is string => !!c)
        .map((c) => c.charAt(0).toUpperCase() + c.slice(1)),
    ),
  ].slice(0, 3);
  return names.length ? names.join(", ") : undefined;
};

// Dimensions are seeded ONLY from products written by an mm-normalizing
// ingest pipeline — the web clipper (`clipper: true`) and MetaSku batches
// (`metaSkuExtracted: true`) both run convertDimensionToMm before writing,
// so their numeric axes are guaranteed millimetres (the schedule's canonical
// unit). Legacy catalog dims are 3D-asset scale (an unmarked meters/cm mix)
// and legacy string dims are free text — seeding either as mm would put wrong
// numbers on a procurement deliverable, so they stay blank.
const seedDimensions = (
  product: ProductInterface,
): FFEScheduleDimensions | undefined => {
  const p = product as ProductInterface & {
    clipper?: boolean;
    metaSkuExtracted?: boolean;
  };
  if (p?.clipper !== true && p?.metaSkuExtracted !== true) return undefined;
  const d = p.dimension as unknown as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return undefined;
  const mm = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
  const dims: FFEScheduleDimensions = {};
  const width = mm(d.width);
  // Product D lives in the catalog's legacy `length`; some older clipper
  // docs kept `depth` itself. No `unit` is ever seeded — display follows
  // the project-owned presentation.units.
  const depth = mm(d.depth) ?? mm(d.length);
  const height = mm(d.height);
  if (width !== undefined) dims.width = width;
  if (depth !== undefined) dims.depth = depth;
  if (height !== undefined) dims.height = height;
  return Object.keys(dims).length ? dims : undefined;
};

// Builder-created user materials can carry base64 data URIs as their texture
// files (cloudflareCDN passes data: URLs through untouched). Persisting one
// into the flat items[] array would push the schedule doc toward Firestore's
// 1MB limit, so only hosted URLs are cached — hydration still renders the
// live product thumbnail either way. Exported for every cachedThumbnail
// writer (toScheduleItem here, refresh/duplicate/identity paths in the
// schedule editor store) so the guard can't be bypassed.
export const persistableThumbnail = (
  product: ProductInterface,
  source: FFEScheduleItemSource,
): string | undefined => {
  const thumb = scheduleItemThumbnail(product, source);
  return thumb && !thumb.startsWith("data:") ? thumb : undefined;
};

// Map a product (productsV2 or userProducts) into a new line item. Seeds the
// editable FF&E/spec layer best-effort from the product's optional sourcing
// metadata — values stay editable per-schedule, the product is not snapshotted.
function toScheduleItem(
  product: ProductInterface,
  source: FFEScheduleItemSource,
  order: number,
  scheduleCurrency: string | undefined,
): FFEScheduleItem {
  const md: Partial<NewProductMetadata> = product?.metadata || {};
  // Seed the price only when the product's currency is unknown or matches
  // the schedule's — a €100 listPrice must not silently become $100.
  const currencyMatches =
    !md.priceCurrency ||
    md.priceCurrency.toUpperCase() ===
      (scheduleCurrency || "USD").toUpperCase();
  // A 0 (or negative) catalog listPrice is placeholder data, not a price —
  // seeding it would put a meaningless RRP on the item's financials.
  const listPrice =
    currencyMatches && isRealPrice(md.listPrice) ? md.listPrice : undefined;
  // A clipped listing's price fills in only where the catalog fields are
  // blank — never over a curated listPrice / priceUnit.
  const clipPrice =
    listPrice == null && !md.priceUnit
      ? seedClipPrice(product, scheduleCurrency)
      : undefined;
  // availableFinishes is a legacy metadata field not on NewProductMetadata.
  const finishes = (md as any).availableFinishes;
  const finish =
    (Array.isArray(finishes) && seedText(finishes[0])) ||
    // The clipper writes a single `metadata.finish`.
    seedText((md as any).finish) ||
    // A created paint's finish IS its preset ("behr-eggshell-enamel" →
    // "Eggshell"); the doc carries no finish text anywhere else.
    seedPaintFinish(product) ||
    undefined;
  return {
    itemId: generateID(),
    source,
    refId: product.id,
    cachedThumbnail: persistableThumbnail(product, source),
    quantity: 1,
    unit: clipPrice?.unit ?? normalizeUnit(md.priceUnit),
    status: "Draft",
    leadTime: normalizeLeadTime(md.leadTime),
    pricing:
      listPrice != null
        ? { listPrice }
        : clipPrice
          ? { listPrice: clipPrice.listPrice }
          : undefined,
    finish,
    color: seedColor(product?.color),
    material: seedMaterial(product, md),
    dimensions: seedDimensions(product),
    order,
  };
}

export interface OffCatalogProductFields {
  name: string;
  supplier?: string;
  sku?: string;
  productLink?: string;
  description?: string;
  thumbnail?: string; // hosted https URL (upload first via uploadFileToGCloud)
}

/**
 * Why an add was refused. `decorative` = generic board decoration that can't
 * be a specification line (#3906); callers must not report it as a duplicate.
 */
export type AddProductRefusal = "duplicate" | "decorative";

export type AddProductResult =
  | {
      ok: true;
      items: FFEScheduleItem[];
      /** The row minted by THIS add — never locate it by array position. */
      item: FFEScheduleItem;
      product: ProductInterface;
    }
  | { ok: false; reason: AddProductRefusal };

/**
 * Create a REAL userProducts doc from off-catalog fields — so the product
 * hydrates and is reusable like any other. Extracted from
 * `addProductToSchedule` so target-free surfaces (the collection detail
 * page's custom product) can mint one without a schedule; `pipeline` tags the
 * creating surface.
 */
export async function createOffCatalogUserProduct(
  offCatalog: OffCatalogProductFields,
  uid: string,
  pipeline: string = "ffe-schedule",
): Promise<ProductInterface> {
  if (!uid) throw new Error("uid is required to create an off-catalog product");
  if (!offCatalog?.name?.trim())
    throw new Error("name is required to create an off-catalog product");
  const product: ProductInterface = {
    id: generateID(),
    name: offCatalog.name.trim(),
    productType: ProductType.ANY,
    objectStatus: ObjectStatusType.USER_CREATED,
    dimension: null,
    supplier: offCatalog.supplier,
    sku: offCatalog.sku,
    productLink: offCatalog.productLink,
    description: offCatalog.description,
    refImages: offCatalog.thumbnail ? [offCatalog.thumbnail] : undefined,
    isStaticObject: false,
    uid,
    pipeline,
    updatedAt: Date.now(),
  };
  validateProduct(product);
  await uploadAndSave(product.id, "userProducts", uid, deepClone(product));
  return product;
}

/**
 * The single add-item entry point. Pass a catalog/userProducts product, or
 * off-catalog fields + uid to first create a REAL userProducts doc (tagged
 * pipeline: 'ffe-schedule' so it hydrates and is reusable like any product).
 * Rejects generic board decoration and duplicates (same source + refId).
 * Returns the next items array — the caller persists via saveFFESchedule and
 * surfaces the rejection (snackbar), never silently drops.
 */
export async function addProductToSchedule(
  schedule: FFEScheduleInterface,
  input:
    | { product: ProductInterface; source: FFEScheduleItemSource }
    | { offCatalog: OffCatalogProductFields; uid: string },
  // Per-section adds: the Section stamped on the minted row at mint time —
  // never rewritten onto some position of the returned array afterwards.
  opts?: { categoryOverride?: string },
): Promise<AddProductResult> {
  let product: ProductInterface;
  let source: FFEScheduleItemSource;
  if ("offCatalog" in input) {
    product = await createOffCatalogUserProduct(input.offCatalog, input.uid);
    source = "userProduct";
  } else {
    product = input.product;
    source = input.source;
  }
  // Before the duplicate check: a decorative accessory shouldn't be on the
  // schedule at all, so "already on it" would be the wrong thing to tell the
  // user about a re-add. Off-catalog mints are ProductType.ANY and can never
  // trip this.
  if (isDecorativeAccessory(product)) return { ok: false, reason: "decorative" };
  const exists = schedule.items.some(
    (i) => i.refId === product.id && i.source === source,
  );
  if (exists) return { ok: false, reason: "duplicate" };
  const override = normalizeCategoryOverride(opts?.categoryOverride);
  const minted = {
    ...toScheduleItem(
      product,
      source,
      schedule.items.length,
      schedule.currency,
    ),
    ...(override ? { categoryOverride: override } : {}),
  };
  return {
    ok: true,
    items: [...schedule.items, minted],
    item: minted,
    product,
  };
}

/**
 * Web-clipper entry point: scrape a product URL into the caller's userProducts
 * via the backend (POST /api/clipper/save-from-url — it dedups against
 * userProducts + productsV2 and returns the existing doc when the listing is
 * already known, source: "catalog" when it matched the shared catalog), then
 * add the product to the schedule. Re-clipping an already-added URL resolves
 * to the same productId, so it rejects as a duplicate like any other re-add.
 */
export async function addProductFromUrl(
  schedule: FFEScheduleInterface,
  url: string,
): Promise<AddProductResult> {
  const clipped = await clipProductFromUrl(url);
  return addProductToSchedule(schedule, clipped);
}

/**
 * The target-free half of the clipper flow: scrape a URL into a product doc
 * (the backend owns creation/dedup — the userProducts doc exists globally
 * regardless of what surface asked). Extracted from `addProductFromUrl` so
 * the collection detail page's Add-from-URL can clip without a schedule.
 * PBR maps are requested for every surface (#4018).
 */
export async function clipProductFromUrl(url: string): Promise<{
  product: ProductInterface;
  source: FFEScheduleItemSource;
  /** An existing product was reused; the backend generated nothing. */
  cached: boolean;
}> {
  const user = getAuth().currentUser;
  if (!user) throw new Error("You must be signed in.");
  const token = await user.getIdToken();
  const resp = await fetch(
    `${getSearchApiBaseUrl()}/api/clipper/save-from-url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ url, generatePbr: true }),
    },
  );
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
  const product = data?.product as ProductInterface;
  if (!product?.id) throw new Error("Scrape returned no product.");
  return {
    product,
    source: data.source === "catalog" ? "catalog" : "userProduct",
    cached: data?.cached === true,
  };
}

export interface AddProductsResult {
  added: number;
  duplicates: number;
  /** Refs that no longer resolve to a product doc in their source collection. */
  missing: number;
  /**
   * Resolved products refused as generic board decoration (#3906) — never
   * folded into `duplicates`, which would falsely claim they're on the
   * schedule. Callers surface this separately or not at all, but the count is
   * always honest.
   */
  blocked: number;
  /** The saved schedule when something was added; the loaded one otherwise. */
  schedule: FFEScheduleInterface;
}

/** Source-qualified product reference for bulk adds. */
export interface ScheduleProductRef {
  id: string;
  source: FFEScheduleItemSource;
}

/**
 * Bulk-add products to a schedule BY source-qualified ID — for surfaces where
 * the schedule editor store isn't mounted (library page, board material
 * sheet). Loads the schedule fresh, appends every resolvable SPECIFIABLE
 * product through addProductToSchedule (same price/lead-time seeding and
 * duplicate rules as the editor; generic board decoration is partitioned out
 * into `blocked`), and persists ONCE with the optimistic-concurrency base; on
 * FFEScheduleConflictError (another tab wrote mid-add) the whole batch is
 * re-run against a fresh load rather than clobbering the other writer.
 * The write-access check mirrors archiveFFESchedule: UX-only — firestore
 * rules remain the real boundary.
 */
export async function addProductsToSchedule(
  scheduleId: string,
  productRefs: ScheduleProductRef[],
  userId: string,
  permissions: Permissions | null,
  // Per-section adds (library ?addToSection=): stamped on each newly added
  // row. Applied inside attempt() so the conflict retry keeps it.
  opts?: { categoryOverride?: string },
): Promise<AddProductsResult> {
  const categoryOverride = normalizeCategoryOverride(opts?.categoryOverride);
  if (!scheduleId) throw new Error("Schedule id is required");
  const seen = new Set<string>();
  const refs = productRefs.filter((ref) => {
    if (!ref?.id) return false;
    const key = `${ref.source}:${ref.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (refs.length === 0) throw new Error("No products selected");
  // Fail fast on a bad source (callers include untyped JSX): a misroute
  // would silently land in `missing`, or worse, persist a non-enum source
  // that the editor's source-qualified hydration misroutes forever.
  for (const ref of refs) {
    if (!(ref.source in SOURCE_DB))
      throw new Error(`Unknown product source "${ref.source}"`);
  }

  // Resolve by the `id` FIELD with the same batched fetchByIds the material
  // sheet and schedule hydration use — a ref that renders anywhere resolves
  // here too (pipeline-written userProducts docs can carry a doc ID that
  // differs from their id field), and N refs cost ceil(N/30) queries per
  // source instead of one round trip each. The first schedule load is
  // independent of the product fetches, so it shares the round trip; the
  // conflict retry reloads fresh.
  const idsFor = (source: FFEScheduleItemSource) =>
    refs.filter((r) => r.source === source).map((r) => r.id);
  const [fetchedBySource, firstLoaded] = await Promise.all([
    Promise.all(
      SOURCES.map((source) => fetchByIds(SOURCE_DB[source](), idsFor(source))),
    ),
    getFFEScheduleById(scheduleId, userId, permissions),
  ]);
  const bySource = Object.fromEntries(
    SOURCES.map((source, i) => [
      source,
      new Map(fetchedBySource[i].map((p) => [p.id, p])),
    ]),
  ) as Record<FFEScheduleItemSource, Map<string, ProductInterface>>;
  const resolved = refs.flatMap((ref) => {
    const product = bySource[ref.source].get(ref.id);
    return product ? [{ product, source: ref.source }] : [];
  });
  const missing = refs.length - resolved.length;
  // Partitioned ONCE, outside attempt(): product docs don't change between
  // conflict retries, so counting inside the retry loop would double-report.
  const products = resolved.filter((p) => !isDecorativeAccessory(p.product));
  const blocked = resolved.length - products.length;

  // Resolved once — a schedule's projectId can't change between conflict
  // retries, so the currency lookup never needs a second round trip.
  let projectPromise: ReturnType<typeof getProjectSchemeById> | null = null;

  const attempt = async (
    preloaded: FFEScheduleInterface | null,
  ): Promise<AddProductsResult> => {
    const loaded =
      preloaded ?? (await getFFEScheduleById(scheduleId, userId, permissions));
    if (!loaded) throw new Error("Schedule not found");
    const hasWrite = hasAccessToResource(
      permissions,
      scheduleId,
      "write",
      "ffeSchedule",
      loaded.uid || "",
      userId,
      loaded.design_firm_id,
      loaded,
    );
    if (!hasWrite)
      throw new Error("You don't have permission to edit this schedule");
    // Price seeding respects the PROJECT currency (#3593) like the editor
    // does — passed only into the per-product mapping, never persisted onto
    // the schedule doc (which keeps its own stored currency untouched).
    projectPromise ??= getProjectSchemeById(loaded.projectId).catch(() => null);
    const project = await projectPromise;
    const seedCurrency = project?.currency || loaded.currency;
    const base = loaded.updatedAt;
    let working: FFEScheduleInterface = loaded;
    let added = 0;
    let duplicates = 0;
    for (const { product, source } of products) {
      const res = await addProductToSchedule(
        { ...working, currency: seedCurrency },
        { product, source },
        { categoryOverride },
      );
      if (res.ok) {
        working = { ...working, items: res.items };
        added += 1;
      } else {
        duplicates += 1;
      }
    }
    if (added === 0)
      return { added, duplicates, missing, blocked, schedule: working };
    // Declare the Section so it persists (and keeps its position) like one
    // made with "+ Create Section" even after its rows are removed.
    if (categoryOverride)
      working = {
        ...working,
        sections: declareSection(working.sections, categoryOverride),
      };
    const saved = await saveFFESchedule(working, base);
    return { added, duplicates, missing, blocked, schedule: saved };
  };

  try {
    return await attempt(firstLoaded);
  } catch (e) {
    if (e instanceof FFEScheduleConflictError) return attempt(null);
    throw e;
  }
}

/**
 * Bulk-add catalog (productsV2) products to a schedule BY ID — the library
 * page's "Add to Schedule" flow. Thin wrapper over addProductsToSchedule.
 */
export async function addCatalogProductsToSchedule(
  scheduleId: string,
  productIds: string[],
  userId: string,
  permissions: Permissions | null,
  opts?: { categoryOverride?: string },
): Promise<AddProductsResult> {
  return addProductsToSchedule(
    scheduleId,
    productIds.map((id) => ({ id, source: "catalog" as const })),
    userId,
    permissions,
    opts,
  );
}

export interface RemoveProductsResult {
  removed: number;
  /** The saved schedule when something was removed; the loaded one otherwise. */
  schedule: FFEScheduleInterface;
}
export type RemoveCatalogProductsResult = RemoveProductsResult;

const refKey = (source: FFEScheduleItemSource, refId: string): string =>
  `${source}:${refId}`;

/**
 * Remove items BY source-qualified product ref — the add modes' un-add,
 * where the editor store isn't mounted. Drops the matching rows (at most one
 * per ref, so editor-duplicated copies with per-row edits survive), renumbers
 * `order`, persists once with the optimistic-concurrency base and re-runs
 * against a fresh load on conflict. Write-access check is UX-only.
 */
export async function removeProductsFromSchedule(
  scheduleId: string,
  productRefs: ScheduleProductRef[],
  userId: string,
  permissions: Permissions | null,
): Promise<RemoveProductsResult> {
  if (!scheduleId) throw new Error("Schedule id is required");
  const keys = new Set<string>();
  for (const ref of productRefs) {
    if (!ref?.id) continue;
    if (!(ref.source in SOURCE_DB))
      throw new Error(`Unknown product source "${ref.source}"`);
    keys.add(refKey(ref.source, ref.id));
  }
  if (keys.size === 0) throw new Error("No products to remove");

  const attempt = async (): Promise<RemoveProductsResult> => {
    const loaded = await getFFEScheduleById(scheduleId, userId, permissions);
    if (!loaded) throw new Error("Schedule not found");
    const hasWrite = hasAccessToResource(
      permissions,
      scheduleId,
      "write",
      "ffeSchedule",
      loaded.uid || "",
      userId,
      loaded.design_firm_id,
      loaded,
    );
    if (!hasWrite)
      throw new Error("You don't have permission to edit this schedule");
    const consumed = new Set<string>();
    const kept = [...loaded.items]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((i) => {
        const key = refKey(i.source, i.refId);
        if (!keys.has(key) || consumed.has(key)) return true;
        consumed.add(key);
        return false;
      });
    const removed = loaded.items.length - kept.length;
    if (removed === 0) return { removed, schedule: loaded };
    const items = kept.map((item, index) =>
      item.order === index ? item : { ...item, order: index },
    );
    const saved = await saveFFESchedule({ ...loaded, items }, loaded.updatedAt);
    return { removed, schedule: saved };
  };

  try {
    return await attempt();
  } catch (e) {
    if (e instanceof FFEScheduleConflictError) return attempt();
    throw e;
  }
}

/**
 * Catalog-only wrapper over removeProductsFromSchedule — the library page's
 * un-add and the destination picker's undo receipt.
 */
export async function removeCatalogProductsFromSchedule(
  scheduleId: string,
  productIds: string[],
  userId: string,
  permissions: Permissions | null,
): Promise<RemoveCatalogProductsResult> {
  return removeProductsFromSchedule(
    scheduleId,
    productIds.map((id) => ({ id, source: "catalog" as const })),
    userId,
    permissions,
  );
}

// ---------------- product identity edits ----------------

// The whitelist + write path live in userProductsApi (shared with My
// Library's identity dialog); re-exported so schedule UI keeps one import.
export {
  PRODUCT_IDENTITY_FIELDS,
  type ProductIdentityField,
  type ProductIdentityPatch,
} from "./userProductsApi";

export function canEditScheduleProduct(
  item: Pick<FFEScheduleItem, "source">,
  product: ProductInterface | undefined,
  uid: string | undefined | null,
): boolean {
  return (
    item.source === "userProduct" && canEditUserProductIdentity(product, uid)
  );
}

/**
 * Write-through identity edit on the caller's OWN userProducts doc (edits
 * propagate to every schedule referencing it — that's the point). Catalog
 * rows are read-only (fork-on-edit is the planned v2); everything else is
 * enforced by updateUserProductIdentity.
 */
export async function updateScheduleProduct(
  item: FFEScheduleItem,
  product: ProductInterface,
  patch: ProductIdentityPatch,
): Promise<void> {
  if (item.source === "catalog")
    throw new Error("Catalog products are read-only");
  await updateUserProductIdentity(product, patch);
}
